import { Button } from '#app/components/Buttons/Button.js';
import { useDrawer } from '#app/components/Drawer/hooks/useDrawer.js';
import { SlideUpDrawerV2 } from '#app/components/Drawer/SlideUpDrawerV2/SlideUpDrawerV2.js';
import { Link } from '#app/components/Link/index.js';
import { ModalV2 } from '#app/components/Modals/V2/components/ModalV2.js';
import { Text } from '#app/components/Primitives/Text.js';
import { useIsMobile } from '#app/hooks/useIsMobile.js';
import {
  Checkbox,
  DrawerActionsContainer,
  DrawerContainer,
  DrawerSubheader,
  MobileFullscreenBackButton,
  MobileFullscreenChildrenContainer,
  StepProgress,
  StepProgressText,
  ToggleLabel,
} from './ModalTemplateWrapper.styles.js';
import {
  type ButtonCTAProps,
  type LinkButtonReturnProps,
  type ModalTemplateWrapperProps,
} from './types.js';
import { ChevronLeftIcon } from '@contra/icons/icons';
import { useRef } from 'react';

const maybeGetButtonLinkProps = (
  props?: ButtonCTAProps,
): LinkButtonReturnProps | undefined => {
  if (props && props.linkButtonProps) {
    if ('href' in props.linkButtonProps) {
      const response: LinkButtonReturnProps<'anchor'> = {
        as: 'a',
        href: props.linkButtonProps.href,
        target: props.linkButtonProps.target,
      };
      return response;
    } else if ('to' in props.linkButtonProps) {
      const response: LinkButtonReturnProps<'link'> = {
        as: Link,
        target: props.linkButtonProps.target,
        to: props.linkButtonProps.to,
      };
      return response;
    }
  }

  return undefined;
};

/**
 * ModalTemplateWrapper is a wrapper around the ModalV2 component that provides automatic conversion from drawer on mobile to a modal on desktop.
 * @param props
 * @param props.betaTag - Boolean to enable the beta tag displayed next to the Modal title
 * @param props.overrideBackgroundColor - Optional prop to override the background color of the modal. It uses our theme keys.
 * @param props.proTag - Boolean to enable the pro tag displayed next to the Modal title
 * @param props.progress - The progress of the modal, used to display a progress bar
 * @param props.children - Custom content to be rendered in the modal
 * @param props.desktopModalSize - The size of the modal on desktop
 * @param props.isFullscreenOnMobile - Whether or not the modal should be fullscreen on mobile, i.e it requires keyboard interaction
 * @param props.isDismissible - Whether or not the modal can be dismissed by clicking outside of it
 * @param props.ariaLabel - The aria label for the modal
 * @param props.headerHero - Custom hero image to be rendered in the modal header
 * @param props.ctaOptions - The CTAs to be used in the modal
 * @param props.icon - The icon to be rendered in the modal header
 * @param props.headerText - The header text for the modal
 * @param props.headerTextStyle - The header text for the modal
 * @param props.overflowX - Whether or not to allow overflow on the x axis, unset to allow overflowing elements
 * @param props.subheaderText - The subheader text for the modal
 * @param props.closeIconColor - The color of the close icon
 * @param props.customHeader - Custom content to be rendered at the top of the modal header
 * @param props.hideCloseIcon - Whether or not to hide the close icon
 * @param props.noMargin - Whether to remove the margin from the modal body
 * @param props.addMobileKeyboardPadding - Defaults to true. Use to add padding so all content remains accessible on mobile browsers when the keyboard is open. Can be disabled in the case of a fullscreen modal that doesn't use the keyboard at all.
 * @param props.isOpen - Whether or not the modal is open
 * @param props.leftAlignTextWithHero - Whether or not to left align the text with the hero image
 * @param props.lockPointerEvents - Disabled pointer events on body element, adds pointer events to modal
 * @param props.onClose - The function to close the modal
 * @param props.preventAutoFocus - Prevent focus auto focus
 * @param props.preventFocusContain - Prevent focus from being scoped to the modal
 * @param props.customZIndex - Custom z-index for the ModalV2 component
 * @param props.blurBackdrop - Whether or not to blur the backdrop
 * @param props.hideBackButton - Whether or not to hide the back button
 * @param props.fullscreenMobileContainerRef - A ref to the fullscreen mobile container
 * @param props.desktopModalRef - A ref to the desktop modal
 * @param props.customBackdropOpacity - Custom opacity value for the backdrop
 * @param props.customBelowModalContent - Custom content to render outside of the modal dialog at the bottom
 */

export const ModalTemplateWrapper = ({
  addMobileKeyboardPadding = true,
  ariaLabel,
  betaTag = false,
  blurBackdrop = false,
  children,
  closeIconColor,
  ctaOptions,
  customBackdropOpacity,
  customBelowModalContent,
  customCloseIconTopOffset,
  customHeader,
  customZIndex,
  desktopModalRef,
  desktopModalSize,
  fullscreenMobileContainerRef,
  headerHero,
  headerText,
  headerTextStyle,
  hideBackButton = false,
  hideCloseIcon,
  icon,
  isDismissible,
  isFullscreenOnMobile,
  isOpen,
  leftAlignTextWithHero = false,
  lockPointerEvents = false,
  noMargin = false,
  onClose,
  overflowX,
  overrideBackgroundColor,
  overrideBackgroundImageUrl,
  overrideBoxShadow,
  preventAutoFocus,
  preventFocusContain,
  progress,
  progressSteps,
  proTag = false,
  subheaderText,
}: ModalTemplateWrapperProps) => {
  const drawerRef = useRef<HTMLDivElement>(null);
  const { drawerProps } = useDrawer({
    drawerRef,
    isDismissable: isDismissible,
    isOpen,
    onToggle: onClose,
  });

  const isGraphicModal = Boolean(headerHero);
  const hasTextSecondaryCTA =
    ctaOptions?.secondaryCTA?.variant === 'V2Link' ||
    ctaOptions?.secondaryCTA?.variant === 'V2DestructiveLink';

  const isMobile = useIsMobile();
  const shouldCenter = isGraphicModal && !leftAlignTextWithHero;

  const modalActionSpacing: 'center' | 'flex-end' | 'space-between' =
    hasTextSecondaryCTA
      ? 'space-between'
      : shouldCenter
        ? 'center'
        : 'flex-end';

  const showCheckbox =
    ctaOptions?.checkboxCTA && !ctaOptions.checkboxCTA.isToggle;
  const showToggle = ctaOptions?.checkboxCTA && ctaOptions.checkboxCTA.isToggle;

  if (isMobile) {
    if (isFullscreenOnMobile) {
      return (
        <ModalV2
          ariaLabel={ariaLabel}
          blurBackdrop={blurBackdrop}
          customZIndex={customZIndex}
          isDismissible={false}
          isOpen={isOpen}
          lockPointerEvents={lockPointerEvents}
          onClose={onClose}
          overflowX={overflowX}
          overrideBackgroundColor={overrideBackgroundColor}
          overrideBackgroundImageUrl={overrideBackgroundImageUrl}
          overrideBoxShadow={overrideBoxShadow}
          preventAutoFocus={preventAutoFocus}
          preventFocusContain={preventFocusContain}
          size="fullscreen"
        >
          {progress === undefined ? null : (
            <ModalV2.Progress percent={progress} />
          )}
          {customHeader ? <>{customHeader}</> : null}
          {headerHero ? <div>{headerHero}</div> : null}
          {hideBackButton && hideCloseIcon ? null : (
            <ModalV2.Header marginForCloseButton={!hideCloseIcon}>
              {hideBackButton ? (
                <ModalV2.Close
                  closeIconColor={closeIconColor}
                  customTopOffset={customCloseIconTopOffset}
                  onClose={onClose}
                />
              ) : (
                <MobileFullscreenBackButton onClick={onClose}>
                  <ChevronLeftIcon size={24} />
                  <Text>Back</Text>
                </MobileFullscreenBackButton>
              )}
            </ModalV2.Header>
          )}
          <ModalV2.Body
            noMargin={noMargin}
            size="fullscreen"
          >
            <MobileFullscreenChildrenContainer
              addMobileKeyboardPadding={addMobileKeyboardPadding}
              ref={fullscreenMobileContainerRef}
            >
              {icon || headerText ? (
                <ModalV2.Title
                  betaTag={betaTag}
                  headerTextStyle={headerTextStyle}
                  icon={icon}
                  justifyContent={shouldCenter ? 'center' : 'flex-start'}
                  proTag={proTag}
                >
                  {headerText}
                </ModalV2.Title>
              ) : null}
              {subheaderText ? (
                <ModalV2.Subheader textAlign={shouldCenter ? 'center' : 'left'}>
                  {subheaderText}
                </ModalV2.Subheader>
              ) : null}
              {children}
              {showCheckbox ? (
                <Checkbox
                  onClick={ctaOptions?.checkboxCTA?.handleOnClick}
                  preChecked={ctaOptions?.checkboxCTA?.default}
                >
                  {ctaOptions?.checkboxCTA?.label}
                </Checkbox>
              ) : null}
              {showToggle ? (
                <ToggleLabel>
                  <Checkbox
                    isToggle
                    onClick={ctaOptions?.checkboxCTA?.handleOnClick}
                    preChecked={ctaOptions?.checkboxCTA?.default}
                  />
                  {ctaOptions?.checkboxCTA?.label}
                </ToggleLabel>
              ) : null}
              {ctaOptions ? (
                <ModalV2.Actions
                  fullWidth={ctaOptions.fullWidth}
                  justifyContent={modalActionSpacing}
                >
                  {ctaOptions.secondaryCTA ? (
                    <Button
                      disabled={ctaOptions.secondaryCTA.isDisabled}
                      icon={ctaOptions.secondaryCTA.icon}
                      isLoading={ctaOptions.secondaryCTA.isLoading}
                      onClick={ctaOptions.secondaryCTA.handleOnClick}
                      variant={
                        ctaOptions.secondaryCTA.variant ?? 'V2TertiaryOutline'
                      }
                      {...maybeGetButtonLinkProps(ctaOptions.secondaryCTA)}
                      {...ctaOptions.secondaryCTA.additionalProps}
                    >
                      {ctaOptions.secondaryCTA.label}
                    </Button>
                  ) : null}
                  {ctaOptions.mobileTertiaryCTA
                    ? ctaOptions.mobileTertiaryCTA
                    : null}
                  {ctaOptions.primaryCTA ? (
                    <Button
                      disabled={ctaOptions.primaryCTA.isDisabled}
                      isLoading={ctaOptions.primaryCTA.isLoading}
                      onClick={ctaOptions.primaryCTA.handleOnClick}
                      variant={ctaOptions.primaryCTA.variant ?? 'V2Primary'}
                      {...maybeGetButtonLinkProps(ctaOptions.primaryCTA)}
                      {...ctaOptions.primaryCTA.additionalProps}
                    >
                      {ctaOptions.primaryCTA.label}
                    </Button>
                  ) : null}
                </ModalV2.Actions>
              ) : null}
            </MobileFullscreenChildrenContainer>
          </ModalV2.Body>
        </ModalV2>
      );
    }

    return (
      <SlideUpDrawerV2
        {...drawerProps}
        ariaLabel={ariaLabel}
        betaTag={betaTag}
        closeIconColor={closeIconColor}
        customHeader={customHeader}
        drawerIcon={icon}
        drawerLabel={headerText}
        headerHero={headerHero}
        hideCloseIcon={hideCloseIcon}
        isOpen={isOpen}
        onClose={onClose}
        preventFocusContain={preventFocusContain}
        proTag={proTag}
      >
        {subheaderText ? (
          <DrawerSubheader textStyle="bodySmall">
            {subheaderText}
          </DrawerSubheader>
        ) : null}
        {children ? <DrawerContainer>{children}</DrawerContainer> : null}
        {ctaOptions ? (
          <DrawerActionsContainer>
            {showCheckbox ? (
              <Checkbox
                onClick={ctaOptions?.checkboxCTA?.handleOnClick}
                preChecked={ctaOptions?.checkboxCTA?.default}
              >
                {ctaOptions?.checkboxCTA?.label}
              </Checkbox>
            ) : null}
            {showToggle ? (
              <ToggleLabel>
                <Checkbox
                  isToggle
                  onClick={ctaOptions?.checkboxCTA?.handleOnClick}
                  preChecked={ctaOptions?.checkboxCTA?.default}
                />
                {ctaOptions?.checkboxCTA?.label}
              </ToggleLabel>
            ) : null}
            {ctaOptions.mobileTertiaryCTA ? ctaOptions.mobileTertiaryCTA : null}
            {ctaOptions.primaryCTA ? (
              <Button
                disabled={ctaOptions.primaryCTA.isDisabled}
                isLoading={ctaOptions.primaryCTA.isLoading}
                onClick={ctaOptions.primaryCTA.handleOnClick}
                variant={ctaOptions.primaryCTA.variant ?? 'V2Primary'}
                {...maybeGetButtonLinkProps(ctaOptions.primaryCTA)}
                {...ctaOptions.primaryCTA.additionalProps}
              >
                {ctaOptions.primaryCTA.label}
              </Button>
            ) : null}
            {ctaOptions.secondaryCTA ? (
              <Button
                disabled={ctaOptions.secondaryCTA.isDisabled}
                icon={ctaOptions.secondaryCTA.icon}
                isLoading={ctaOptions.secondaryCTA.isLoading}
                onClick={ctaOptions.secondaryCTA.handleOnClick}
                variant={ctaOptions.secondaryCTA.variant ?? 'V2TertiaryOutline'}
                {...maybeGetButtonLinkProps(ctaOptions.secondaryCTA)}
                {...ctaOptions.secondaryCTA.additionalProps}
              >
                {ctaOptions.secondaryCTA.label}
              </Button>
            ) : null}
          </DrawerActionsContainer>
        ) : null}
      </SlideUpDrawerV2>
    );
  }

  return (
    <ModalV2
      ariaLabel={ariaLabel}
      blurBackdrop={blurBackdrop}
      customBackdropOpacity={customBackdropOpacity}
      customBelowModalContent={customBelowModalContent}
      customZIndex={customZIndex}
      isDismissible={isDismissible}
      isOpen={isOpen}
      lockPointerEvents={lockPointerEvents}
      onClose={onClose}
      overflowX={overflowX}
      overrideBackgroundColor={overrideBackgroundColor}
      overrideBackgroundImageUrl={overrideBackgroundImageUrl}
      overrideBoxShadow={overrideBoxShadow}
      preventAutoFocus={preventAutoFocus}
      preventFocusContain={preventFocusContain}
      size={desktopModalSize}
    >
      {progress === undefined ? null : <ModalV2.Progress percent={progress} />}
      {customHeader ? <>{customHeader}</> : null}
      {headerHero ? <ModalV2.Hero>{headerHero}</ModalV2.Hero> : null}

      {hideCloseIcon ? null : (
        <ModalV2.Close
          closeIconColor={closeIconColor}
          customTopOffset={customCloseIconTopOffset}
          onClose={onClose}
        />
      )}
      {icon || headerText ? (
        <ModalV2.Header marginForCloseButton={!hideCloseIcon}>
          <ModalV2.Title
            betaTag={betaTag}
            headerTextStyle={headerTextStyle}
            icon={icon}
            justifyContent={shouldCenter ? 'center' : 'flex-start'}
            proTag={proTag}
          >
            {headerText}
          </ModalV2.Title>
        </ModalV2.Header>
      ) : null}
      <ModalV2.Body
        desktopModalRef={desktopModalRef}
        noMargin={noMargin}
      >
        {subheaderText ? (
          <ModalV2.Subheader textAlign={shouldCenter ? 'center' : 'left'}>
            {subheaderText}
          </ModalV2.Subheader>
        ) : null}
        {children}
        {ctaOptions ? (
          <ModalV2.Actions
            fullWidth={ctaOptions?.fullWidthOnDesktop}
            justifyContent={modalActionSpacing}
          >
            {progressSteps ? (
              <StepProgress>
                <StepProgressText>
                  step {progressSteps.currentStep}/{progressSteps.totalSteps}
                </StepProgressText>
              </StepProgress>
            ) : null}
            {showCheckbox ? (
              <Checkbox
                onClick={ctaOptions?.checkboxCTA?.handleOnClick}
                preChecked={ctaOptions?.checkboxCTA?.default}
              >
                {ctaOptions?.checkboxCTA?.label}
              </Checkbox>
            ) : null}
            {showToggle ? (
              <ToggleLabel>
                <Checkbox
                  isToggle
                  onClick={ctaOptions?.checkboxCTA?.handleOnClick}
                  preChecked={ctaOptions?.checkboxCTA?.default}
                />
                {ctaOptions?.checkboxCTA?.label}
              </ToggleLabel>
            ) : null}

            {ctaOptions.desktopTertiaryCTA
              ? ctaOptions.desktopTertiaryCTA
              : null}

            {ctaOptions.secondaryCTA ? (
              <Button
                aria-label={ctaOptions.secondaryCTA.ariaLabel}
                disabled={ctaOptions.secondaryCTA.isDisabled}
                icon={ctaOptions.secondaryCTA.icon}
                isLoading={ctaOptions.secondaryCTA.isLoading}
                onClick={ctaOptions.secondaryCTA.handleOnClick}
                variant={ctaOptions.secondaryCTA.variant ?? 'V2TertiaryOutline'}
                {...maybeGetButtonLinkProps(ctaOptions.secondaryCTA)}
                {...ctaOptions.secondaryCTA.additionalProps}
              >
                {ctaOptions.secondaryCTA.label}
              </Button>
            ) : null}
            {ctaOptions.primaryCTA ? (
              <Button
                aria-label={ctaOptions.primaryCTA.ariaLabel}
                disabled={ctaOptions.primaryCTA.isDisabled}
                isLoading={ctaOptions.primaryCTA.isLoading}
                onClick={ctaOptions.primaryCTA.handleOnClick}
                variant={ctaOptions.primaryCTA.variant ?? 'V2Primary'}
                {...maybeGetButtonLinkProps(ctaOptions.primaryCTA)}
                {...ctaOptions.primaryCTA.additionalProps}
              >
                {ctaOptions.primaryCTA.label}
              </Button>
            ) : null}
          </ModalV2.Actions>
        ) : null}
      </ModalV2.Body>
    </ModalV2>
  );
};
